import type { RouteNameFromPath, RoutePathSchema, RoutesNamesList } from '@typed-router'

/**
 * **PathFor** – turn a *route name* into its *only allowed* path template.
 *
 * @template {RoutesNamesList} N
 *
 * @example
 * type LoginPath = PathFor<'login'>   // 👉  "/login"
 */
export type PathFor<N extends RoutesNamesList> = {
  [P in RoutePathSchema]: RouteNameFromPath<P> extends N ? P : never;
}[RoutePathSchema]

/**
 * **StrictRoute** – tagged-union that guarantees a `name ⇄ path` match.
 *
 * A value has to pick **one whole member** of the union, so
 * passing a path that does not belong to the chosen name is a
 * compile-time error.
 *
 * @example
 * const ok:  StrictRoute = { name: 'login', path: '/login' }            // ✓
 * const bad: StrictRoute = { name: 'login', path: '/forgot-password' }  // ❌
 */
export type StrictRoute = { [N in RoutesNamesList]: { name: N, path: PathFor<N> } }[RoutesNamesList]

export const USER_LOGGED_ROUTES_PATH_PREFIX = 'o-'

/**
 * **_APP_ROUTES** – your route constant.
 *
 * Declared `as const` → IDE autocompletes the keys (`.login`, `.forgotPassword`, …)
 * Checked with `satisfies Record<string, StrictRoute>` →
 *   each entry must respect the `name ⇄ path` relationship.
 *
 * @example
 * _APP_ROUTES.login.path         // "/login"
 * _APP_ROUTES.forgotPassword     // { name: "forgot-password", path: "/forgot-password" }
 */
export const _APP_ROUTES = {
  invite: { name: 'account-invite', path: '/account/invite' },
  login: { name: 'login', path: '/login' },
  forgotPassword: { name: 'forgot-password', path: '/forgot-password' },
  redirect: { name: 'redirect', path: '/redirect' },
  resetPassword: { name: 'reset-password', path: '/reset-password' },
  onboardingRegister: { name: 'onboarding-register', path: '/onboarding/register' },
  onboardingInvite: { name: 'onboarding-invite', path: '/onboarding/invite' },
  onboardingCreate: { name: 'onboarding-create', path: '/onboarding/create' },
  onboardingSelect: { name: 'onboarding-select', path: '/onboarding/select' },
  transactionDetails: {
    name: 'transaction-details',
    path: '/o-:organisationId/transactions/:transactionId/details',
  },
  dealMemo: { name: 'deal-memo', path: '/o-:organisationId/deals/:dealId/memo' },
  deals: { name: 'deals', path: '/o-:organisationId/deals' },
  dealDetails: { name: 'deal-details', path: '/o-:organisationId/deals/:dealId/details' },
  dealManage: { name: 'deal-manage', path: '/o-:organisationId/deals/:dealId/manage' },
  investors: { name: 'subscribers', path: '/o-:organisationId/investors' },
  portfolio: { name: 'portfolio', path: '/o-:organisationId/portfolio' },
  compliance: {
    name: 'compliance-dynamic',
    path: `/o-:organisationId/account/compliance`,
  },
  complianceInit: {
    name: 'compliance-init',
    path: `/o-:organisationId/account/compliance/init`,
  },
  complianceMember: {
    name: 'compliance-dynamic-member',
    path: `/o-:organisationId/account/compliance/member/:memberId`,
  },
  complianceMemberInit: {
    name: 'compliance-member-init',
    path: `/o-:organisationId/account/compliance/member/:memberId/init`,
  },
  account: {
    name: 'account',
    path: `/o-:organisationId/account`,
  },
  accountMembers: {
    name: 'account-members',
    path: `/o-:organisationId/account/members`,
  },
  accountAccounts: {
    name: 'account-accounts',
    path: `/o-:organisationId/account/accounts`,
  },
  accountDocuments: {
    name: 'account-documents',
    path: `/o-:organisationId/account/documents`,
  },
  accountForms: {
    name: 'account-forms',
    path: `/o-:organisationId/account/forms`,
  },
  accountDeals: {
    name: 'account-deals',
    path: `/o-:organisationId/account/deals`,
  },
  accountInvestments: {
    name: 'account-investments',
    path: `/o-:organisationId/account/investments`,
  },
  adminAccountMonitoring: {
    name: 'admin-account-monitoring',
    path: `/admin/account/o-:organisationId/monitoring`,
  },
  adminDeals: { name: 'admin-deals', path: '/admin/deals' },
  adminDealDetails: { name: 'admin-deal-details', path: '/admin/deals/:dealId/details' },
  adminDealManage: { name: 'admin-manage-deal', path: '/admin/deals/:dealId/manage' },
  adminDealGenerateSubscriptionAgreement: {
    name: 'admin-deal-generate-subscription-agreement',
    path: '/admin/deals/:dealId/generate/subscription-agreement',
  },
  adminDealGenerateTradeOrder: {
    name: 'admin-deal-generate-trade-order',
    path: '/admin/deals/:dealId/generate/trade-order',
  },
  adminOrganisations: { name: 'admin-organisations', path: '/admin/organisations' },
  adminOrganisationsAll: { name: 'admin-organisations-all', path: '/admin/organisations/all' },
  adminOrganisationsUnset: { name: 'admin-organisations-unset', path: '/admin/organisations/unset' },
  adminOrganisationsGroup: {
    name: 'admin-organisations-group',
    path: '/admin/organisations/groups/:group',
  },
  adminTransactions: { name: 'admin-transactions', path: '/admin/transactions' },
  adminInvestors: { name: 'admin-investors', path: '/admin/investors' },
  adminComplianceOnboarding: { name: 'admin-compliance-onboarding', path: '/admin/compliance/onboarding' },
  adminBankDetails: { name: 'admin-bank-details-all', path: '/admin/bank-details' },
  adminBankDetailsProducts: { name: 'admin-bank-details-products', path: '/admin/bank-details/products' },
  adminBankDetailsOrganisations: { name: 'admin-bank-details-organisations', path: '/admin/bank-details/organisations' },
  // @ts-expect-error / is not included in the paths
  home: { name: 'home', path: '/' },
} satisfies Record<string, StrictRoute>

export const APP_ROUTES: Record<keyof typeof _APP_ROUTES, (typeof _APP_ROUTES)[keyof typeof _APP_ROUTES]> = _APP_ROUTES

export type AppRouteKey = keyof typeof _APP_ROUTES
export type AppRoute = (typeof _APP_ROUTES)[AppRouteKey]

export const EXTERNAL_ROUTES = {
  termsAndConditions: {
    url: 'https://kapital.inc/terms-conditions',
    label: 'Terms of Services',
  },
  privacyPolicy: {
    url: 'https://www.kapital.inc/privacy-policy',
    label: 'Privacy & Data Policy',
  },
  legalNotice: {
    url: 'https://www.kapital.inc/legal-notice',
    label: 'Legal Notice',
  },
  imprint: {
    url: 'https://www.kapital.inc/legal/imprint',
    label: 'Imprint',
  },
} as const
